import { Component, ViewChild, OnInit, OnDestroy, ChangeDetectorRef, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { SettingsOffcanvasComponent } from '../../settings-offcanvas/settings-offcanvas.component';
import { Subscription } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { SchoolService } from '../../core/services/school.service';
import { DocumentItem, GetDocumentsResponse, AddStudentResponse, ApplicationData } from '../../core/models/auth.model';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';

import { StudentProfilePreviewModalComponent } from '../student-profile-preview-modal/student-profile-preview-modal.component';


@Component({
  selector: 'app-add-student-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, SettingsOffcanvasComponent, TranslateModule, StudentProfilePreviewModalComponent],
  templateUrl: './add-student-profile.component.html',
  styleUrls: ['./add-student-profile.component.css']
})
export class AddStudentProfileComponent implements OnInit, OnDestroy {

  @ViewChild('studentProfileForm') studentProfileForm!: NgForm;
  formData: { [key: string]: any } = {};

  private photoFile: File | null = null;
  private statementRiderMedicalConditionFile: File | null = null;
  private medicalCertificateDownSyndromeFile: File | null = null;
  private medicalCertificateHeartDiseaseFile: File | null = null;
  private statementReleaseIndemnityFile: File | null = null;

  specialNeedsConditions = [
    {
      id: 'autism',
      labelKey: 'ADD_STUDENT.PART_B.AUTISM_LABEL',
      severityNgModelName: 'asd',
      remarksNgModelName: 'asd_remark',
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    },
    {
      id: 'adhd',
      labelKey: 'ADD_STUDENT.PART_B.ADHD_LABEL',
      severityNgModelName: 'adhd',
      remarksNgModelName: 'adhd_remark',
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    },
    {
      id: 'sld',
      labelKey: 'ADD_STUDENT.PART_B.SLD_LABEL',
      severityNgModelName: 'sld',
      remarksNgModelName: 'sld_remark',
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    },
    {
      id: 'intelDisability',
      labelKey: 'ADD_STUDENT.PART_B.ID_LABEL',
      severityNgModelName: 'ind',
      remarksNgModelName: 'ind_remark',
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    },
    {
      id: 'cd',
      labelKey: 'ADD_STUDENT.PART_B.CD_LABEL',
      severityNgModelName: 'comd',
      remarksNgModelName: 'comd_remark',
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    },
    {
      id: 'vi',
      labelKey: 'ADD_STUDENT.PART_B.VI_LABEL',
      severityNgModelName: 'visi',
      remarksNgModelName: 'visi_remark',
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    },
    {
      id: 'hi',
      labelKey: 'ADD_STUDENT.PART_B.HI_LABEL',
      severityNgModelName: 'heari',
      remarksNgModelName: 'heari_remark',
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    },
    {
      id: 'bed',
      labelKey: 'ADD_STUDENT.PART_B.BED_LABEL',
      severityNgModelName: 'bed',
      remarksNgModelName: 'bed_remark',
      hasSpecify: true,
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    },
    {
      id: 'pd',
      labelKey: 'ADD_STUDENT.PART_B.PD_LABEL',
      severityNgModelName: 'phyd',
      remarksNgModelName: 'phyd_remark',
      noteKey: 'ADD_STUDENT.PART_B.PD_NOTE',
      apiValueMap: { 'Mild': 0, 'Moderate': 1, 'Severe': 2 }
    }
  ];

  selectedPhotoFileName: string | null = null;
  imagePreviewUrl: string | ArrayBuffer | null = null;

  availableDocuments: DocumentItem[] = [];
  isLoadingDocuments = false;
  documentsListError: string | null = null;

  isDownloadingDocument: { [docIdentifier: string]: boolean } = {};
  documentDownloadError: { [docIdentifier: string]: string | null } = {};

  private readonly fileBaseUrl = environment.fileStorageBaseUrl;

  consentLabels = {
    fixedPrefix: '',
    riderInfoConsent: '我同意 ...',
    rehabReleaseConsent: '我同意 ...',
    photoVideoConsent: '我同意 ...',
    rdaLessonArrangementConsent: '我同意 ...',
    nameOver18: '',
    nameBelow18: '',
    DISAGREEPREFIX: '',
    medical_epilepsy: '',
    medical_downs: '',
    medical_heart_: '',

  };

  private langChangeSubscription: Subscription | undefined;

  isSubmitting: boolean = false;

  nationalities = ['HK', 'CN', 'US', 'GB', 'CA', 'FR', 'JP', 'KR', 'NP', 'PK', 'PS', 'RU', 'SA', 'SG', 'OTHER'];

  hasPhotoError: boolean = false;
  hasStatementEpilepsyError: boolean = false;
  hasMedicalDownSyndromeError: boolean = false;
  hasMedicalHeartDiseaseError: boolean = false;
  hasStatementReleaseIndemnityError: boolean = false;

  hasConsentError: boolean = false;

  applications: ApplicationData[] = [];
  isLoadingApplications = false;
  applicationsError: string | null = null;
  isPreviewModalVisible = false;
  previewData: { [key: string]: any } = {};
  private finalFormData: FormData | null = null; // 用于存储最终要提交的 FormData

  private applicationsSubscription: Subscription | undefined;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private schoolService: SchoolService,
    public translate: TranslateService,
    private http: HttpClient,
    private authService: AuthService
  ) {
    if (!this.fileBaseUrl) {
      console.error('CRITICAL: environment.fileStorageBaseUrl is not defined! Document links will be broken.');
    }
  }

  ngOnInit(): void {
    this.loadApplicationsData();
    this.specialNeedsConditions.forEach(condition => {
      this.formData[condition.severityNgModelName] = null;
      this.formData[condition.remarksNgModelName] = '';

    });

    this.formData['statement_rider_medical_condition'] = null;
    this.formData['medical_certificate_down_syndrome'] = null;
    this.formData['medical_certificate_heart_disease'] = null;
    this.formData['statement_release_indemnity'] = null;

    this.loadAvailableDocuments();
    this.langChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      console.log('Language changed in AddStudentProfileComponent, updating consent labels.');
      this.updateConsentLabels();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
  clearSpecialNeedSelection(severityNgModelName: string, remarksNgModelName: string): void {
    this.formData[severityNgModelName] = null;
    this.formData[remarksNgModelName] = '';

    this.cdr.detectChanges();
    console.log(`已清除 ${severityNgModelName} 的选择和备注。`);
  }
  clearRadioSelection(controlName: string): void {
    if (this.studentProfileForm && this.studentProfileForm.form.get(controlName)) {
      this.studentProfileForm.form.get(controlName)?.setValue(null);
      console.log(`已清除单选组 '${controlName}' 的选择。`);
    }
  }

  clearNonAmbulatorySelection(): void {
    if (this.studentProfileForm) {
      this.studentProfileForm.form.get('nonAmbulatoryChoice')?.setValue(null);

      this.studentProfileForm.form.get('non_ambulatory_other')?.setValue('');
      console.log(`已清除 '不可走动' 部分的选择和备注。`);
    }
  }
  loadApplicationsData(): void {
    this.isLoadingApplications = true;
    this.applicationsError = null;

    this.applicationsSubscription = this.authService.activeApplications$.subscribe({
      next: (apps: ApplicationData[] | null) => {
        this.isLoadingApplications = false;
        if (apps && apps.length > 0) {
          this.applications = apps;
          console.log('HomeComponent has received applications from AuthService:', this.applications);
        } else {
          this.applications = [];
          this.applicationsError = this.translate.instant('HOME.NO_APPLICATIONS_FOUND');
          console.log('HomeComponent: No active term application data found.');
        }
      },
      error: (err) => {
        this.isLoadingApplications = false;
        this.applications = [];
        this.applicationsError = this.translate.instant('HOME.ERROR_LOADING_APPLICATIONS_FROM_SERVICE');
        console.error('HomeComponent: Error fetching applications from AuthService:', err);
      }
    });
  }
  loadAvailableDocuments(): void {
    this.isLoadingDocuments = true;
    this.documentsListError = null;
    this.availableDocuments = [];

    this.schoolService.getDocuments().subscribe({
      next: (response: GetDocumentsResponse) => {
        this.isLoadingDocuments = false;
        if (response.status === 'ok' && response.data && response.data.documents) {
          this.availableDocuments = response.data.documents;
          console.log('Available documents loaded for consent labels:', this.availableDocuments);
          this.updateConsentLabels();
          if (this.availableDocuments.length === 0) {
            this.documentsListError = this.translate.instant('ADD_STUDENT.PART_D.NO_DOCUMENTS_FOUND');
          }
        } else {
          const apiError = (response.message || (response as any).error);
          this.documentsListError = apiError || this.translate.instant('ADD_STUDENT.PART_D.ERROR_LOADING_DOCUMENTS');
          this.updateConsentLabels();
        }
      },
      error: (err) => {
        this.isLoadingDocuments = false;
        this.documentsListError = this.translate.instant('ADD_STUDENT.PART_D.ERROR_FETCHING_DOCUMENTS');
        this.updateConsentLabels();
        console.error('Error fetching documents:', err);
      }
    });
  }

  private getLocalizedDocName(docType: string, fallbackTextKey: string): string {
    if (!this.availableDocuments || this.availableDocuments.length === 0) {
      return this.translate.instant(fallbackTextKey) + this.translate.instant('ADD_STUDENT.PART_C.DOC_NOT_FOUND_SUFFIX');
    }
    const doc = this.availableDocuments.find(d => d.type === docType);
    if (!doc) {
      return this.translate.instant(fallbackTextKey) + this.translate.instant('ADD_STUDENT.PART_C.DOC_NOT_FOUND_SUFFIX');
    }

    const currentLang = this.translate.currentLang;
    if (currentLang === 'zh-CN' && doc.name_zh_cn) {
      return doc.name_zh_cn;
    } else if (currentLang === 'zh-HK' && doc.name_zh_hk) {
      return doc.name_zh_hk;
    } else if (doc.name_en) {
      return doc.name_en;
    }
    return this.translate.instant(fallbackTextKey);
  }

  private updateConsentLabels(): void {
    const fixedPrefix = this.translate.instant('ADD_STUDENT.PART_C.I_AGREE_PREFIX');
    const DISAGREE_PREFIX = this.translate.instant('ADD_STUDENT.PART_C.I_DISAGREE_PREFIX');
    this.consentLabels.riderInfoConsent = this.getLocalizedDocName('health_assessment', 'ADD_STUDENT.PART_C.RIDER_INFO_CONSENT_LABEL');
    this.consentLabels.rehabReleaseConsent = this.getLocalizedDocName('rehab_release_indemnity', 'ADD_STUDENT.PART_C.REHAB_RELEASE_CONSENT_LABEL');
    const nameBelow18 = this.getLocalizedDocName('photo_taking_below_18', 'ADD_STUDENT.PHOTO_CONSENT_BELOW_18');
    const nameOver18 = this.getLocalizedDocName('photo_taking_over_18', 'ADD_STUDENT.PHOTO_CONSENT_OVER_18');
    this.consentLabels.photoVideoConsent = `${nameBelow18} / ${nameOver18}`;
    this.consentLabels.nameBelow18 = `${nameBelow18}`;
    this.consentLabels.nameOver18 = `${nameOver18}`;
    this.consentLabels.fixedPrefix = `${fixedPrefix}`;
    this.consentLabels.DISAGREEPREFIX = `${DISAGREE_PREFIX}`;

    // 将以下三行替换为使用 getLocalizedDocName 方法
    this.consentLabels.medical_epilepsy = this.getLocalizedDocName('medical_epilepsy', 'ADD_STUDENT.PART_D.STATEMENT_MEDICAL_EPILEPSY_LABEL');
    this.consentLabels.medical_downs = this.getLocalizedDocName('medical_downs', 'ADD_STUDENT.PART_D.MEDICAL_CERTIFICATE_DOWN_SYNDROME_LABEL');
    this.consentLabels.medical_heart_ = this.getLocalizedDocName('medical_heart_', 'ADD_STUDENT.PART_D.MEDICAL_CERTIFICATE_HEAR_DISEASE_LABEL');

    this.consentLabels.rdaLessonArrangementConsent = this.getLocalizedDocName('rda_lesson_arrangement', 'ADD_STUDENT.PART_C.RDA_LESSON_ARRANGEMENT_LABEL');

    console.log('Updated consent labels:', this.consentLabels);
    this.cdr.detectChanges();
  }
  public getDocumentFileDetails(documentIdentifier: string): { url: string | null, filename: string } {
    let defaultFilename = `${documentIdentifier.replace(/[^a-zA-Z0-9]/g, '_') || 'document'}.pdf`;
    if (!this.fileBaseUrl) {
      console.error('fileBaseUrl is not configured for getDocumentFileDetails.');
      return { url: null, filename: defaultFilename };
    }
    if (!this.availableDocuments || this.availableDocuments.length === 0) {
      return { url: null, filename: defaultFilename };
    }
    const foundDoc = this.availableDocuments.find(d => d.type === documentIdentifier);

    if (foundDoc && foundDoc.file) {
      let relativePath = foundDoc.file;
      const cleanBaseUrl = this.fileBaseUrl.replace(/\/$/, '');
      const cleanRelativePath = relativePath.replace(/^\//, '');
      const fullUrl = `${cleanBaseUrl}/${cleanRelativePath}`;
      const filenameFromPath = relativePath.split('/').pop();
      const suggestedFilename = filenameFromPath || foundDoc.name_en?.replace(/[^a-zA-Z0-9_.\-]/g, '_') + '.pdf' || defaultFilename;
      return { url: fullUrl, filename: suggestedFilename };
    }
    console.warn(`Template document with type '${documentIdentifier}' not found or has no file path.`);
    return { url: null, filename: defaultFilename };
  }
  triggerDocumentDownload(documentIdentifier: string): void {
    const docDetails = this.getDocumentFileDetails(documentIdentifier);
    const fullUrl = docDetails.url;

    if (fullUrl) {
      console.log(`Opening document '${documentIdentifier}' from:`, fullUrl);
      window.open(fullUrl, '_blank');
    } else {
      console.warn(`URL for document '${documentIdentifier}' is not available.`);
      alert(this.translate.instant('ADD_STUDENT.PART_D.DOCUMENT_URL_NOT_FOUND'));
    }
  }
  getDays(): number[] { return Array.from({ length: 31 }, (_, i) => i + 1); }
  getMonths(): string[] { return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']; }
  getYears(): number[] { const currentYear = new Date().getFullYear(); const startYear = currentYear - 100; const yearRange = 101; return Array.from({ length: yearRange }, (_, i) => startYear + i).reverse(); }

  calculateAge(event: any): void {
    const birthDateValue = event.target.value;
    if (birthDateValue) {
      const birthDate = new Date(birthDateValue);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      this.formData['age'] = age >= 0 ? age : null;
      if (this.studentProfileForm.form.controls['age']) {
        this.studentProfileForm.form.controls['age'].setValue(this.formData['age']);
      }
      this.cdr.detectChanges();
    } else {
      this.formData['age'] = null;
      if (this.studentProfileForm.form.controls['age']) {
        this.studentProfileForm.form.controls['age'].setValue(null);
      }
      this.cdr.detectChanges();
    }
  }
  submitProfile(): void {
    let validationFailed = false;

    if (!this.photoFile) {
      this.hasPhotoError = true;
      validationFailed = true;
    } else {
      this.hasPhotoError = false;
    }
    // if (!this.statementRiderMedicalConditionFile) {
    //   this.hasStatementEpilepsyError = true;
    //   validationFailed = true;
    // } else {
    //   this.hasStatementEpilepsyError = false;
    // }
    // if (!this.medicalCertificateDownSyndromeFile) {
    //   this.hasMedicalDownSyndromeError = true;
    //   validationFailed = true;
    // } else {
    //   this.hasMedicalDownSyndromeError = false;
    // }
    // if (!this.medicalCertificateHeartDiseaseFile) {
    //   this.hasMedicalHeartDiseaseError = true;
    //   validationFailed = true;
    // } else {
    //   this.hasMedicalHeartDiseaseError = false;
    // }
    if (!this.statementReleaseIndemnityFile) {
      this.hasStatementReleaseIndemnityError = true;
      validationFailed = true;
    } else {
      this.hasStatementReleaseIndemnityError = false;
    }
    // 同意书勾选必填验证
    const formValue = this.studentProfileForm.value;
    // if (!formValue.riderInfoConsent || !formValue.rehabReleaseConsent ||  !formValue.rdaLessonArrangementConsent) {
    
    if (!formValue.riderInfoConsent || !formValue.rdaLessonArrangementConsent) {
      this.hasConsentError = true;
      validationFailed = true;
    } else {
      this.hasConsentError = false;
    }

    if (!this.studentProfileForm.value.ageDisclaimer) {
      validationFailed = true;
    }

    if (this.studentProfileForm.invalid || validationFailed) {
      console.warn('表单验证失败，请检查所有必填项和文件上传。');
      this.studentProfileForm.form.markAllAsTouched();
      this.expandFirstInvalidAccordion(); // 展开第一个无效字段或文件错误所在的 accordion
      return;
    }
    console.log('提交时 formData 的值:', this.formData);
    console.log('提交时 studentProfileForm.value 的值:', this.studentProfileForm.value);
    this.previewData = {
      ...this.studentProfileForm.value,
      consentLabels: this.consentLabels,
      photo: this.photoFile,
      statement_rider_medical_condition: this.statementRiderMedicalConditionFile,
      medical_certificate_down_syndrome: this.medicalCertificateDownSyndromeFile,
      medical_certificate_heart_disease: this.medicalCertificateHeartDiseaseFile,
      statement_release_indemnity: this.statementReleaseIndemnityFile
    };

    // 2. 准备最终要提交到后端的 FormData
    this.prepareFinalFormData();

    console.log('在 add-student-profile 中，将要传递给预览弹窗的数据:', this.previewData);
    // 3. 显示预览弹窗
    this.isPreviewModalVisible = true;

  }
  private prepareFinalFormData(): void {
    const formDataToSend = new FormData();
    const formValue = this.studentProfileForm.value;

    if (this.applications && this.applications.length > 0) {
      const applicationId = this.applications[0].id;
      formDataToSend.append('application_id', applicationId.toString());
    } else {
      // ... 错误处理 ...
      return;
    }
    const allFormData = { ...formValue, ...this.formData };
    for (const key in allFormData) {
      if (allFormData.hasOwnProperty(key)) {
        let value = allFormData[key];
        if (key === 'nationality') {
          if (value === 'OTHER') {
            formDataToSend.append('nationality', allFormData['other_nationality'] || '');
          } else {
            formDataToSend.append('nationality', value);
          }
          continue; 
        }
        if (key === 'other_nationality') {
          continue; 
        }
        if (key === 'ageDisclaimer') {
          continue; 
        }
        const condition = this.specialNeedsConditions.find(c => c.severityNgModelName === key);
        if (condition && condition.apiValueMap && value !== null && value !== undefined) {
          const mappedValue = condition.apiValueMap[value as 'Mild' | 'Moderate' | 'Severe'];
          if (mappedValue !== undefined) {
            value = mappedValue;
          }
        }
        if (key === 'riderInfoConsent') {
          formDataToSend.append('data_use', value ? '1' : '0');
          continue;
        } else if (key === 'rehabReleaseConsent') {
          // formDataToSend.append('tnc', value ? '1' : '0');
          continue;
        } else if (key === 'photoVideoConsent') {
          formDataToSend.append('photo_use_rider', value ? '1' : '0');
          continue;
        } else if (key === 'rdaLessonArrangementConsent') {
          formDataToSend.append('rda_policy', value ? '1' : '0');
          continue;
        }

        if (key === 'weight' && value !== null && value !== undefined && value !== '') {
          value = `${value} kg`;
        } else if (key === 'height' && value !== null && value !== undefined && value !== '') {
          value = `${value} cm`;
        } else if (key === 'head_size' && value !== null && value !== undefined && value !== '') {
          value = `${value} cm`;
        }

        if (key === 'nonAmbulatoryChoice') {
          console.log('nonAmbulatoryChoice------------', value);
          if (value === 'other') {
            formDataToSend.append('non_ambulatory', '');
            formDataToSend.append('non_ambulatory_other', allFormData['non_ambulatory_other'] || 'other');
          } else if (value === 'wheelchair') {
            formDataToSend.append('non_ambulatory', value);
            formDataToSend.append('non_ambulatory_other', '');

          } else {
            formDataToSend.append('non_ambulatory', value);
            formDataToSend.append('non_ambulatory_other', '');
          }
          continue;
        }
        // if (key === 'non_ambulatory_other') {
        //   continue;
        // }
        if (key.startsWith('ambulatory')) {
          if (key === 'ambulatoryIndependent') {
            const ambulatoryOptions: string[] = [];
            if (allFormData.ambulatoryIndependent) ambulatoryOptions.push('independent');
            if (allFormData.ambulatoryAssistanceNeeded) ambulatoryOptions.push('assistance_needed');
            if (allFormData.ambulatoryUnaided) ambulatoryOptions.push('unaided');
            if (allFormData.ambulatoryCrutches) ambulatoryOptions.push('crutches');
            if (allFormData.ambulatoryWalkingStick) ambulatoryOptions.push('walking_stick');
            if (allFormData.ambulatoryWalker) ambulatoryOptions.push('walker');
            // if (allFormData.ambulatory_other && allFormData.ambulatory_other.trim() !== '') {
            //   ambulatoryOptions.push(allFormData.ambulatory_other.trim());
            // }
            formDataToSend.append('ambulatory', ambulatoryOptions.join(','));
            formDataToSend.append('ambulatory_other', allFormData.ambulatory_other || '');
          }
          continue;
        }
        // if (key === 'ambulatory_other') {
        //   continue;
        // }
        if (value === null || value === undefined) {
          formDataToSend.append(key, '');
        } else {
          formDataToSend.append(key, value);
        }
      }
    }
    if (this.photoFile) { formDataToSend.append('photo', this.photoFile, this.photoFile.name); }
    if (this.statementRiderMedicalConditionFile) { formDataToSend.append('statement_rider_medical_condition', this.statementRiderMedicalConditionFile, this.statementRiderMedicalConditionFile.name); }
    if (this.medicalCertificateDownSyndromeFile) { formDataToSend.append('medical_certificate_down_syndrome', this.medicalCertificateDownSyndromeFile, this.medicalCertificateDownSyndromeFile.name); }
    if (this.medicalCertificateHeartDiseaseFile) { formDataToSend.append('medical_certificate_heart_disease', this.medicalCertificateHeartDiseaseFile, this.medicalCertificateHeartDiseaseFile.name); }
    if (this.statementReleaseIndemnityFile) {
      formDataToSend.append('statement_release_indemnity', this.statementReleaseIndemnityFile, this.statementReleaseIndemnityFile.name);
    }
    this.finalFormData = formDataToSend;
  }

  handleFinalSubmit(): void {
    this.isPreviewModalVisible = false;
    // console.info('最终提交的数据',this.finalFormData,this.previewData)
    if (!this.finalFormData) {
      console.error('最终提交的数据丢失！');
      return;
    }
    this.isSubmitting = true;
    this.schoolService.addApplicationStudent(this.finalFormData).subscribe({
      next: (response: AddStudentResponse) => {
        this.isSubmitting = false;
        if (response.status === 'ok' && response.student) {
          console.log('学生档案已成功提交:', response.student);
          alert(this.translate.instant('ADD_STUDENT.SUBMIT_SUCCESS_MESSAGE'));
          this.router.navigate(['/student-profile-summary']);
        } else {
          const errorMessage = response.message || this.translate.instant('ADD_STUDENT.SUBMIT_GENERIC_ERROR');
          console.error('提交失败:', errorMessage);
          alert(errorMessage);
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        console.error('提交学生档案时发生错误:', err);
        let userFacingError = this.translate.instant('ADD_STUDENT.SUBMIT_NETWORK_ERROR');
        if (err.status) {
          switch (err.status) {
            case 403: userFacingError = err.error?.error || this.translate.instant('ADD_STUDENT.SUBMIT_ERROR_403'); break;
            case 422:
              if (err.error && err.error.errors) {
                const errorMessages = Object.keys(err.error.errors).map(key => `${key}: ${err.error.errors[key].join(', ')}`).join('\n');
                userFacingError = this.translate.instant('ADD_STUDENT.SUBMIT_ERROR_422') + '\n' + errorMessages;
                this.setFormErrors(err.error.errors);
              } else {
                userFacingError = this.translate.instant('ADD_STUDENT.SUBMIT_ERROR_422_GENERIC');
              }
              break;
            default: userFacingError = this.translate.instant('ADD_STUDENT.SUBMIT_HTTP_ERROR', { status: err.status }); break;
          }
        }
        alert(userFacingError);
      }
    });
  }

  handleCloseModal(): void {
    this.isPreviewModalVisible = false;
  }
  private setFormErrors(errors: any): void {
    if (!this.studentProfileForm || !this.studentProfileForm.controls) {
      return;
    }
    for (const key in errors) {
      if (errors.hasOwnProperty(key)) {
        const control = this.studentProfileForm.controls[key];
        if (control) {
          control.setErrors({ backendError: errors[key].join(', ') });
          control.markAsTouched();
        }
      }
    }
    this.cdr.detectChanges();
  }
  private expandFirstInvalidAccordion(): void {
    if (this.hasPhotoError) {
      const personalInfoAccordion = document.getElementById('personalInfoAccordion');
      if (personalInfoAccordion) {
        const collapseElement = personalInfoAccordion.querySelector('.accordion-collapse');
        if (collapseElement) {
          const bsCollapse = new (window as any).bootstrap.Collapse(collapseElement, { toggle: false });
          bsCollapse.show();
          const photoUploadAreaLabel = personalInfoAccordion.querySelector('label[for="photoFileActual"]');
          if (photoUploadAreaLabel) {
            (photoUploadAreaLabel as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
      }
    }

    if (this.hasConsentError) {
      const guardianConsentAccordion = document.getElementById('guardianConsentAccordion');
      if (guardianConsentAccordion) {
        const collapseElement = guardianConsentAccordion.querySelector('.accordion-collapse');
        if (collapseElement) {
          const bsCollapse = new (window as any).bootstrap.Collapse(collapseElement, { toggle: false });
          bsCollapse.show();
          const firstConsentCheckbox = guardianConsentAccordion.querySelector('input[type="checkbox"]'); // 移除 [required] 限制，因为可能没有required
          if (firstConsentCheckbox) {
            (firstConsentCheckbox as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
      }
    }

    if (this.hasStatementEpilepsyError || this.hasMedicalDownSyndromeError || this.hasMedicalHeartDiseaseError) {
      const guardianDocumentAccordion = document.getElementById('guardianDocumentAccordion');
      if (guardianDocumentAccordion) {
        const collapseElement = guardianDocumentAccordion.querySelector('.accordion-collapse');
        if (collapseElement) {
          const bsCollapse = new (window as any).bootstrap.Collapse(collapseElement, { toggle: false });
          bsCollapse.show();
          const firstFileUploadLabel = guardianDocumentAccordion.querySelector('label[for="statementEpilepsyUpload"], label[for="medicalDownSyndromeUpload"], label[for="medicalHeartDiseaseUpload"]');
          if (firstFileUploadLabel) {
            (firstFileUploadLabel as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
      }
    }

    const accordionItems = Array.from(document.querySelectorAll('.accordion-item'));
    for (const item of accordionItems) {
      const accordionBody = item.querySelector('.accordion-collapse');
      if (accordionBody && this.containsInvalidControl(item)) {
        const collapseElement = item.querySelector('.accordion-collapse');
        const bsCollapse = new (window as any).bootstrap.Collapse(collapseElement, {
          toggle: false
        });
        bsCollapse.show();
        const firstInvalidControl = item.querySelector('.ng-invalid.ng-touched');
        if (firstInvalidControl) {
          (firstInvalidControl as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        break;
      }
    }
  }

  private containsInvalidControl(element: Element): boolean {
    const invalidControls = element.querySelectorAll('.ng-invalid.ng-touched');
    return invalidControls.length > 0;
  }


  clearOtherTextIfUnselected(otherTextFieldName: string, radioValueForOtherOption: string): void {
    setTimeout(() => {
      if (this.studentProfileForm && this.studentProfileForm.controls) {
        const choiceControl = this.studentProfileForm.controls['nonAmbulatoryChoice'];
        const currentChoice = choiceControl ? choiceControl.value : undefined;
        const otherTextInputControl = this.studentProfileForm.controls[otherTextFieldName];
        if (currentChoice !== radioValueForOtherOption && otherTextInputControl) {
          otherTextInputControl.setValue('');
        }
      }
    }, 0);
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      this.photoFile = file;
      this.selectedPhotoFileName = file.name;
      this.hasPhotoError = false;

      const reader = new FileReader();
      reader.onload = () => { this.imagePreviewUrl = reader.result; };

      if (file.type.startsWith('image/')) {
        reader.readAsDataURL(file);
      } else {
        this.imagePreviewUrl = null;
      }
      console.log('选中的照片文件:', file);
    } else {

      this.photoFile = null;
      this.selectedPhotoFileName = null;
      this.imagePreviewUrl = null;
      this.hasPhotoError = true;
    }
  }

  onDocumentFileSelected(event: Event, fileType: string): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      switch (fileType) {
        case 'statement_rider_medical_condition':
          this.statementRiderMedicalConditionFile = file;
          this.formData['statement_rider_medical_condition'] = file.name;
          this.hasStatementEpilepsyError = false;
          break;
        case 'medical_certificate_down_syndrome':
          this.medicalCertificateDownSyndromeFile = file;
          this.formData['medical_certificate_down_syndrome'] = file.name;
          this.hasMedicalDownSyndromeError = false;
          break;
        case 'medical_certificate_heart_disease':
          this.medicalCertificateHeartDiseaseFile = file;
          this.formData['medical_certificate_heart_disease'] = file.name;
          this.hasMedicalHeartDiseaseError = false;
          break;
        case 'statement_release_indemnity':
          this.statementReleaseIndemnityFile = file;
          this.formData['statement_release_indemnity'] = file.name;
          this.hasStatementReleaseIndemnityError = false;
          break;
        default:
          console.warn(`Unknown file type: ${fileType}`);
          break;
      }
      console.log(`选中的用户上传文档 (${fileType}):`, file);
    } else {
      switch (fileType) {
        case 'statement_rider_medical_condition':
          this.statementRiderMedicalConditionFile = null;
          this.formData['statement_rider_medical_condition'] = null;
          this.hasStatementEpilepsyError = true;
          break;
        case 'medical_certificate_down_syndrome':
          this.medicalCertificateDownSyndromeFile = null;
          this.formData['medical_certificate_down_syndrome'] = null;
          this.hasMedicalDownSyndromeError = true;
          break;
        case 'medical_certificate_heart_disease':
          this.medicalCertificateHeartDiseaseFile = null;
          this.formData['medical_certificate_heart_disease'] = null;
          this.hasMedicalHeartDiseaseError = true;
          break;
        case 'statement_release_indemnity':
          this.statementReleaseIndemnityFile = null;
          this.formData['statement_release_indemnity'] = null;
          this.hasStatementReleaseIndemnityError = true; 
          break;

      }
    }
  }
}